import type {
  Tournament,
  Stage,
  StageUnlock,
  ArchivePage,
  ArchiveTournamentItem,
  PredictionMutationResponse,
  SwissPredictionUpsertBody,
  SwissPredictionRemoveBody,
  BracketPredictionUpsertBody,
  BracketPredictionRemoveBody,
  LeaderboardPage,
  LeaderboardMe,
  RewardsResponse,
  RewardLevel,
  ClaimResponse
} from './types'

const PICKEM_PREFIX = '/api/v1/pickem'

const idempotencyHeaders = (idempotencyKey: string) => ({
  'X-Idempotency-Key': idempotencyKey
})

export const usePickemApi = () => {
  const { $api } = useNuxtApp()

  return {
    getActiveTournament: () =>
      $api.get<Tournament | null>(`${PICKEM_PREFIX}/tournaments/active`),

    getLastFinishedTournament: () =>
      $api.get<Tournament | null>(`${PICKEM_PREFIX}/tournaments/last-finished`),

    getTournamentById: (id: Tournament['id']) =>
      $api.get<Tournament>(`${PICKEM_PREFIX}/tournaments/${id}`),

    getTournamentArchive: (
      query: { page?: number; 'per-page'?: number } = {}
    ) =>
      $api.get<ArchivePage>(`${PICKEM_PREFIX}/tournaments/archive`, { query }),

    getStageById: (id: Stage['id']) =>
      $api.get<Stage>(`${PICKEM_PREFIX}/stages/${id}`),

    getStageUnlockConditions: (id: Stage['id']) =>
      $api.get<StageUnlock>(`${PICKEM_PREFIX}/stages/${id}/unlock-conditions`),

    upsertSwissPrediction: (
      body: SwissPredictionUpsertBody,
      idempotencyKey: string
    ) =>
      $api.post<PredictionMutationResponse>(
        `${PICKEM_PREFIX}/predictions/swiss`,
        body,
        { headers: idempotencyHeaders(idempotencyKey) }
      ),

    removeSwissPrediction: (
      body: SwissPredictionRemoveBody,
      idempotencyKey: string
    ) =>
      $api.delete<PredictionMutationResponse>(
        `${PICKEM_PREFIX}/predictions/swiss`,
        {
          body,
          headers: idempotencyHeaders(idempotencyKey)
        }
      ),

    upsertBracketPrediction: (
      body: BracketPredictionUpsertBody,
      idempotencyKey: string
    ) =>
      $api.post<PredictionMutationResponse>(
        `${PICKEM_PREFIX}/predictions/bracket`,
        body,
        { headers: idempotencyHeaders(idempotencyKey) }
      ),

    removeBracketPrediction: (
      body: BracketPredictionRemoveBody,
      idempotencyKey: string
    ) =>
      $api.delete<PredictionMutationResponse>(
        `${PICKEM_PREFIX}/predictions/bracket`,
        {
          body,
          headers: idempotencyHeaders(idempotencyKey)
        }
      ),

    getLeaderboardPage: (
      tournamentId: Tournament['id'],
      query: { page?: number; 'per-page'?: number; search?: string } = {},
      options: { signal?: AbortSignal } = {}
    ) =>
      $api.get<LeaderboardPage>(
        `${PICKEM_PREFIX}/tournaments/${tournamentId}/leaderboard`,
        { query, signal: options.signal }
      ),

    getLeaderboardMe: (
      tournamentId: Tournament['id'],
      options: { signal?: AbortSignal } = {}
    ) =>
      $api.get<LeaderboardMe>(
        `${PICKEM_PREFIX}/tournaments/${tournamentId}/leaderboard/me`,
        { signal: options.signal }
      ),

    getRewards: (tournamentId: Tournament['id']) =>
      $api.get<RewardsResponse>(
        `${PICKEM_PREFIX}/tournaments/${tournamentId}/rewards`
      ),

    claimReward: (levelId: RewardLevel['id'], idempotencyKey: string) =>
      $api.post<ClaimResponse>(
        `${PICKEM_PREFIX}/rewards/${levelId}/claim`,
        undefined,
        { headers: idempotencyHeaders(idempotencyKey) }
      )
  }
}

export type {
  Tournament,
  Stage,
  StageUnlock,
  ArchivePage,
  ArchiveTournamentItem,
  LeaderboardPage,
  LeaderboardMe,
  RewardsResponse,
  RewardLevel,
  ClaimResponse
} from './types'
